import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isEmpty } from '@ember/utils'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import lexileYRange from 'teacher-ui-client/utils/giraffe/lexile-y-range'
import {
  gradePositionRangeFromLexiles,
  lexileRangeFromGradePosition,
} from 'teacher-ui-client/utils/giraffe/lexile-grade-map'
import { reportingPlotLine as plotLine } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import type { IntlService } from 'ember-intl'
import type { Point, SeriesLineOptions } from 'highcharts'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesLine } from 'teacher-ui-client/components/giraffe/primitives/series-line'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'

export interface LexileDetailsAveragesDataItem {
  grade_position: number
  summary: { student_count: number; lexile_gain: number; class_average: number }
  months: {
    month: string
    student_count: number
    lexile_average: Nullable<number>
  }[]
}

interface Args {
  data: LexileDetailsAveragesDataItem
  tooltip: string
  title: string
  product: 're' | 'rex'
  grade: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class LexileDetailsAverages extends Component<Signature> {
  @service intl!: IntlService
  @service gradeSets!: GradeSetsService

  /**
   * Chart Margin.
   * Override the right margin to allow space for plot line labels.
   */
  readonly chartMargin = [null, 120, null, null]

  readonly colorScheme = SummaryBoxColorScheme.LightBlue

  /**
   * List of summary statistics, extracted from the input data.
   * Display lexile growth and average lexile level for the class.
   */
  get summaryStats() {
    const { summary } = this.args.data
    const gain = summary.lexile_gain
    const average = summary.class_average

    return [
      { label: 'Class Average Lexile Growth', value: `+${gain}L` },
      { label: 'Class Average Lexile Level', value: `${average}L` },
    ]
  }

  /**
   * Computed list of column data, extracted from the input data.
   * Formats month data for Highcharts.
   */
  get chartData(): SeriesLineOptions[] {
    const { months } = this.args.data

    const data = months.map((month, index) => {
      return { x: index, y: month.lexile_average, monthData: month }
    })

    return [{ name: 'lexile_average', color: colors.oceanyBlue300, data, type: 'line' }]
  }

  /**
   * Computed list of category data, extracted from the input data.
   * Formats month data for Highcharts for use as column labels.
   * Converts month dates into short names.
   */
  get categories() {
    const monthsData = this.args.data.months ?? []
    return monthsData.map((monthData) => convertDateFormat(monthData.month))
  }

  /**
   * Plot lines to mark the grade bands for the lexile values.
   */
  get plotLines() {
    const product = this.args.product
    const monthsData = this.args.data.months ?? []
    const unfilteredLexiles = monthsData.map((monthData) => monthData.lexile_average)
    const lexiles = unfilteredLexiles.filter((monthData) => !isEmpty(monthData))
    if (isEmpty(lexiles)) return []

    const positionPadding = 1 // add extra plotline to chart
    const gradePositionRange = gradePositionRangeFromLexiles(product, lexiles, positionPadding)

    const plotLines = gradePositionRange.map((gradePosition) => {
      const { min: lexile } = lexileRangeFromGradePosition(product, gradePosition) as { min: number; max: number }
      const label = this.gradeSets.fullNameFromGradeSetByPosition(gradePosition) ?? ''

      if (isEmpty(lexile)) {
        return null
      }

      return plotLine(lexile, label)
    })

    return plotLines.filter((line) => !isEmpty(line))
  }

  /**
   * Set the yRange of the chart to include all lessons for all grades in the data
   */
  get yRange() {
    const product = this.args.product
    const monthsData = this.args.data.months ?? []
    const unfilteredLexiles = monthsData.map((monthData) => monthData.lexile_average)
    const lexiles = unfilteredLexiles.filter((monthData) => !isEmpty(monthData))
    const [min, max] = lexileYRange(product, lexiles)
    return [min, max]
  }

  /**
   * Set the tooltip of the points in the chart to include the student count
   */
  get toolTipFormatter() {
    const studentCountLabel = this.intl.t('reporting.statBoxes.assessmentScoresTotals.studentCount')

    return function (this: Point) {
      if (this.color !== 'transparent') {
        const { monthData } = this as Point & { monthData: { student_count: number } }
        const studentCount = monthData.student_count

        return `<strong>${this.y}L<br>${studentCountLabel}: ${studentCount}</strong>`
      }
    }
  }

  <template>
    <div data-test-giraffe-chart-lexile-details-averages class="relative h-full" ...attributes>
      <ChartInfoTooltip @tooltipContent={{@tooltip}} />
      <LeftSplitChart>
        <:left>
          <SummaryBox class="h-full" @grade={{@grade}} @stats={{this.summaryStats}} @colorScheme={{this.colorScheme}} />
        </:left>
        <:right>
          <SeriesLine
            @title={{@title}}
            @data={{this.chartData}}
            @categories={{this.categories}}
            @yAxisLabel="Lexile level"
            @toolTipUnit="L"
            @toolTipFormatter={{this.toolTipFormatter}}
            @plotLines={{this.plotLines}}
            @chartMargin={{this.chartMargin}}
            @yRange={{this.yRange}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default LexileDetailsAverages
